import React from 'react'

import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import createLogger from '~/src/Lib/Logging'
import { atLeastOneRequired, regexPropType } from '~/src/Lib/PropTypes'
import { memoize } from '~/src/Lib/Utils'

const displayName = 'EntityPrimaryColor'
const logger = createLogger(displayName)

const defaultPrimary = { main: '#bbb' }

const getHarvestRelatedPrimary = props => {
  const { colorsByKey, entity, harvests, phases } = props
  if (!entity.harvest) return colorsByKey.PROP
  const { [entity.harvest]: harvest } = harvests
  if (!harvest) return colorsByKey.PROP
  const { [harvest.currentPhase]: phase } = phases
  if (!phase) return colorsByKey.PROP

  return colorsByKey[phase.phaseType]
}

const getPlantPrimary = props => {
  const { colorsByKey, entity } = props
  if (entity.state && entity.state !== 'TRACKED') {
    return colorsByKey[entity.state] ?? colorsByKey.FLOWER
  }

  return colorsByKey[entity.growthPhase]
}

const getPrimaryColor = memoize(props => {
  const { colorKey, colorsByItem, colorsByKey, entity } = props
  let primary
  if (!entity || (colorKey && colorKey in colorsByKey)) {
    ({ [colorKey]: primary = defaultPrimary } = colorsByKey)
    return { primary }
  }
  // Get item category color for entities with an item prop
  if (entity.item) {
    let item = entity.item.id
    if (!item) {
      item = entity.item
    }
    ({ [item]: primary = defaultPrimary } = colorsByItem)
    return { primary }
  }
  // Get phase color for phase or harvest group
  if (entity?.phaseType || entity?.currentPhase?.phaseType) {
    ({ [entity.phaseType ?? entity.currentPhase?.phaseType]: primary = defaultPrimary } = colorsByKey)
    return { primary }
  }
  if ('growthPhase' in entity && 'state' in entity) {
    primary = getPlantPrimary(props)
    return { primary: primary || defaultPrimary }
  }
  if ('harvest' in entity) {
    primary = getHarvestRelatedPrimary(props)
    return { primary: primary || defaultPrimary }
  }
  if (!entity.uuid) {
    logger.warn('returning default color for', props)
    return { primary: defaultPrimary }
  }
  // Get category color for an item
  if (entity.uuid.startsWith('items')) {
    ({ [entity.category]: primary = defaultPrimary } = colorsByKey)
    return { primary }
  }
  // Get dataType's self-defined color
  if (entity.uuid.startsWith('dataType')) {
    primary = entity.color ? { main: entity.color } : defaultPrimary
    return { primary }
  }
  logger.warn('returning default color for', props)

  return { primary: defaultPrimary }
})

/**
 * Given an entity or color key, calls function children or renders element children with a primary palette
 * suitable for creating a new theme
 * @component
 * @alias EntityPrimaryColor
 * @example
 * import Button from '~/src/UI/Shared/Button'
 * import { ThemeProvider } from '@mui/material/styles'
 * import { buildThemeFromColor } from '~/src/UI/Theme/mui'
 * {() => {
 *   const Wrapper = ({ children, ...passthru }) => (
 *     <EntityPrimaryColor {...passthru}>
 *       {({ primary }) => (
 *         <ThemeProvider
 *           theme={theme => buildThemeFromColor(primary, {
 *             adjustColor: true,
 *             bg: theme.palette.background.dark
 *           })}
 *         >
 *           {children}
 *         </ThemeProvider>
 *       )}
 *     </EntityPrimaryColor>
 *   )
 *   return (
 *     <Box className="default-display" justifyContent="space-between">
 *       <Wrapper colorKey="VEG">
 *         <div className="default-display">
 *           <T.Subtitle>Color by Key</T.Subtitle>
 *           <Button>Primary Color Button</Button>
 *           <T.Bold color="primary">Primary color text</T.Bold>
 *         </div>
 *       </Wrapper>
 *       <Wrapper entity={{ item: 1, uuid: 'things_1' }}>
 *         <div className="default-display">
 *           <T.Subtitle>Color by Entity: entity has item</T.Subtitle>
 *           <Button>Primary Color Button</Button>
 *           <T.Bold color="primary">Primary color text</T.Bold>
 *         </div>
 *       </Wrapper>
 *       <Wrapper entity={{ uuid: 'items_2', category: 'FRESH' }}>
 *         <div className="default-display">
 *           <T.Subtitle>Color by Entity: entity is item</T.Subtitle>
 *           <Button>Primary Color Button</Button>
 *           <T.Bold color="primary">Primary color text</T.Bold>
 *         </div>
 *       </Wrapper>
 *       <Wrapper entity={{ uuid: 'harvests_1', currentPhase: { phaseType: 'DRY' } }}>
 *         <div className="default-display">
 *           <T.Subtitle>Color by Entity: entity is harvest</T.Subtitle>
 *           <Button>Primary Color Button</Button>
 *           <T.Bold color="primary">Primary color text</T.Bold>
 *         </div>
 *       </Wrapper>
 *       <Wrapper entity={{ uuid: 'plants_1', state: 'DESTROYED' }}>
 *         <div className="default-display">
 *           <T.Subtitle>Color by Key: entity is plant</T.Subtitle>
 *           <Button>Primary Color Button</Button>
 *           <T.Bold color="primary">Primary color text</T.Bold>
 *         </div>
 *       </Wrapper>
 *       <Wrapper entity={{ uuid: 'dataTypes_1', color: 'gold' }}>
 *         <div className="default-display">
 *           <T.Subtitle>Color by Key: entity is dataType</T.Subtitle>
 *           <Button>Primary Color Button</Button>
 *           <T.Bold color="primary">Primary color text</T.Bold>
 *         </div>
 *       </Wrapper>
 *     </Box>
 *   )
 * }}
 */
export const ColorComponent = props => {
  const { children } = props
  const primaryProps = getPrimaryColor(props)

  return React.isValidElement(children) ? React.cloneElement(children, primaryProps) : children(primaryProps)
}
ColorComponent.displayName = displayName
ColorComponent.propTypes = {
  /**
   * When children is a function, it is called with an object arg: <pre>{ primary: { dark?: $color, light?: $color, main: $color } }</pre>
   * An element child is cloned, adding a primary prop with the same shape as above.
   */
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.element]).isRequired,
  ...atLeastOneRequired({
    colorKey: regexPropType(/^\w+$/).nullable,
    entity: PropTypes.shape({
      uuid: PropTypes.string.isRequired,
    }),
  }),
}

export default React.memo(props => {
  const connectedProps = useConnect('selectColorsByItem', 'selectColorsByKey', 'selectHarvests', 'selectPhases')

  return <ColorComponent {...connectedProps} {...props} />
})
